import { FC, useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import axios from '../../utils/axios';
import { useAppSelector } from '../../store/hooks';

export type RequestType = 'upgrade' | 'renew' | 'enterprise' | 'pos' | 'pos_renew';

const TITLE: Record<RequestType, string> = {
  upgrade: 'Request an upgrade',
  renew: 'Request renewal',
  enterprise: 'Contact sales · Enterprise',
  pos: 'Request more POS outlets',
  pos_renew: 'Request POS renewal',
};

// Starting text so the form is never empty; the customer can edit it
const defaultMessage = (type: RequestType, planName?: string) => ({
  upgrade: `We would like to upgrade to the ${planName || 'next'} plan. Please contact us with the details.`,
  renew: 'Please renew our Wareozo subscription and share the payment details.',
  enterprise: 'We are interested in the Enterprise plan. Please contact us to discuss our requirements.',
  pos: 'We need more POS outlets. Please contact us with the details.',
  pos_renew: 'Please renew our POS subscription and share the payment details.',
}[type]);

interface IRequestDialogProps {
  type: RequestType;
  // plan picked in the plans dialog
  planCode?: string;
  onClose: () => void;
}

/** Sends an upgrade / renewal / sales request to the Wareozo team (platform panel → Requests). */
const RequestDialog: FC<IRequestDialogProps> = ({ type, planCode: initialPlan, onClose }) => {
  const [plans, setPlans] = useState<{ code: string; name: string }[]>([]);
  const [planCode, setPlanCode] = useState(initialPlan || '');
  // account owner's number, else the signed-in user's
  const { selectedAccount, user } = useAppSelector((state) => state.auth);
  const owner: any = (selectedAccount as any)?.owner;
  const [phone, setPhone] = useState<string>((typeof owner === 'object' && (owner?.phone || owner?.mobile)) || (user as any)?.phone || (user as any)?.mobile || '');
  const [message, setMessage] = useState(defaultMessage(type));
  const [messageEdited, setMessageEdited] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (type !== 'upgrade') return;
    axios.get('/api/v1/subscription/plans')
      .then((response) => setPlans((response.data.plans || []).filter((p: any) => p.code !== 'starter')))
      .catch(() => undefined);
  }, [type]);

  // keep the starting text in step with the chosen plan until the customer edits it
  useEffect(() => {
    if (!messageEdited) setMessage(defaultMessage(type, plans.find((p) => p.code === planCode)?.name));
  }, [type, planCode, plans, messageEdited]);

  const send = async () => {
    setError('');
    setSending(true);
    try {
      const response = await axios.post('/api/v1/subscription/request', { type, planCode: planCode || undefined, phone, message });
      toast.success(response.data.message);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not send the request');
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>{TITLE[type]}</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          {type === 'upgrade' && (
            <Grid item xs={12} md={6}>
              <TextField select label='Plan you want' size='small' fullWidth value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
                {plans.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} md={type === 'upgrade' ? 6 : 12}>
            <TextField label='Phone No.' size='small' fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Message'
              size='small'
              fullWidth
              multiline
              minRows={3}
              value={message}
              onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }}
              placeholder={type === 'enterprise' ? 'Number of users, companies, data volume, integrations you need…' : 'Anything we should know'}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size='small' variant='outlined' onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <LoadingButton size='small' variant='contained' loading={sending} onClick={send} sx={{ textTransform: 'none' }}>Send request</LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDialog;
